import { useState } from "react";
import AutoTuneStatusControl from "@/pages/jobs/auto-tune/AutoTuneStatusControl";
import ClusterAutoTuneSettingsDrawer from "./ClusterAutoTuneSettingsDrawer";
import {
  useClusterAutoTuneRecommendations,
  useClusterAutoTuneStatus,
  useToggleClusterAutoTune,
} from "./hooks";
import type { ClusterRecType } from "./types";

interface Props {
  clusterId: string;
  clusterName: string;
  instanceId: number | string;
  workspaceId?: string;
  // Node types from the cluster detail, threaded to the settings drawer's
  // recommended-state DAG card as context (the recommendation tunes
  // worker count / idle window, not the instance type).
  workerNodeType?: string | null;
  driverNodeType?: string | null;
  // Live worker sizing — fallback for the DAG card when the cluster has
  // no resize recommendation.
  minAutoscaleWorkers?: number | null;
  maxAutoscaleWorkers?: number | null;
  workerCount?: number | null;
}

// Header-level master Auto Tune control for an all-purpose cluster: the
// pill toggle (enable/disable every recommendation) plus the gear that
// opens the settings drawer. Owns the status + recommendations queries
// and the toggle mutation, so the pill and the drawer always read the
// same server truth. Disabling the pill / drawer switches when the
// permission preflight blocks is handled by the shared
// ``AutoTuneStatusControl`` + drawer respectively.
const ClusterAutoTuneControl = ({
  clusterId,
  clusterName,
  instanceId,
  workspaceId,
  workerNodeType,
  driverNodeType,
  minAutoscaleWorkers,
  maxAutoscaleWorkers,
  workerCount,
}: Props) => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const status = useClusterAutoTuneStatus(clusterId, instanceId, workspaceId);
  const recommendations = useClusterAutoTuneRecommendations(clusterId, instanceId, workspaceId);
  const toggle = useToggleClusterAutoTune({ clusterId, instanceId, workspaceId });

  const masterEnabled = status.data?.auto_tune_mode === "enabled";
  const preflight = status.data?.preflight ?? { blocked: false };
  const recs = recommendations.data?.recommendations ?? [];

  const setMaster = (enabled: boolean) => toggle.mutate({ enabled, recType: null });
  const setRec = (recType: ClusterRecType, enabled: boolean) => toggle.mutate({ enabled, recType });

  return (
    <>
      <AutoTuneStatusControl
        enabled={masterEnabled}
        onToggle={setMaster}
        isToggling={toggle.isPending}
        preflight={preflight}
        tasksEnabled={recs.filter(rec => rec.enabled).length}
        tasksTotal={recs.length}
        onOpenSettings={() => setDrawerOpen(true)}
        isLoading={status.isLoading}
      />
      <ClusterAutoTuneSettingsDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        clusterName={clusterName}
        clusterId={clusterId}
        workerNodeType={workerNodeType}
        driverNodeType={driverNodeType}
        minAutoscaleWorkers={minAutoscaleWorkers}
        maxAutoscaleWorkers={maxAutoscaleWorkers}
        workerCount={workerCount}
        blocked={Boolean(preflight.blocked)}
        blockedMessage={preflight.actionable_message}
        recommendations={recs}
        isLoadingRecs={recommendations.isLoading}
        isToggling={toggle.isPending}
        onToggleRec={setRec}
      />
    </>
  );
};

export default ClusterAutoTuneControl;
